import _ from 'underscore';
import Promise from 'bluebird';
import { parseIdFromGridKey, MCCPREFERENCENAME } from '@bingads-webui-universal/custom-column-utils';
import { getEntityFromScope, edmCampaign } from '@bingads-webui/edm-campaign';
import { QueryParam } from '@bingads-webui-react/scope-service';

const expressionToken = /(\+|-|\*|\/|\(|\)| )/;

function updatePreferenceStoreWithColumn(customColumn) {
  const existingColumns = this.getAll();
  const editCustomColumnData = _.defaults({
    id: parseIdFromGridKey(customColumn.id),
  }, customColumn);
  const customColumns = _.reject(existingColumns, col => editCustomColumnData.id === col.id)
    .concat(customColumn);

  this.setPreferencesRecord(this.recordId, customColumns);
}

function getCustomColumnsFromMT(avoidCache) {
  if (avoidCache) {
    edmCampaign['odata.cache'].invalidateTypeCache('Model.CustomColumn');
  }

  return this.getCustomColumnEntity().findAll({ limit: 100 })
    .then(data => _.map(data, column => ({
      description: column.Description,
      expression: _.filter(column.CustomColumnExpression.split(expressionToken), token => token !== '' && token !== ' '),
      format: column.Format,
      id: String(column.Id),
      name: column.Name,
    })));
}

export class CustomColumnServiceWrapper {
  constructor({
    name,
    CustomColumns,
  }) {
    _.extend(this, {
      name,
      CustomColumns,
      isMCCLevel: name === MCCPREFERENCENAME,
    });
  }

  get query() {
    return {
      project: 'CampaignUI',
      grid: this.name,
    };
  }

  get recordId() {
    return (this.getPreferencesRecord() || {}).id || -1;
  }

  getPreferencesRecord() {
    return _.first(this.CustomColumns.filter({ where: this.query }));
  }

  setPreferencesRecord(recordId, customColumns) {
    const promises = [];

    if (recordId === -1) {
      promises.push(this.CustomColumns.create(_.defaults(
        {
          customColumns,
        },
        this.query
      )));
    } else {
      promises.push(this.CustomColumns.update(recordId, { customColumns }));
    }

    return Promise.all(promises).thenReturn();
  }

  warmCache() {
    return this.CustomColumns.findAll({ where: this.query });
  }

  getAll() {
    const record = this.getPreferencesRecord();

    return record ? record.get('customColumns') : [];
  }

  get(id) {
    return _.findWhere(this.getAll(), { id });
  }

  add(column) {
    return this.getCustomColumnEntity().post({
      Name: column.name,
      Description: column.description,
      CustomColumnExpression: column.expression.join(''),
      Format: column.format,
    }).toPromise().then(({ value }) => {
      const id = String(value);

      this.setPreferencesRecord(this.recordId, this.getAll().concat(_.defaults({
        id,
      }, column)));

      return id;
    });
  }

  remove(columnId) {
    const customColumnId = parseIdFromGridKey(columnId);

    this.getCustomColumnEntity().$withKey(customColumnId).destroyAsPromise();
    const existingColumns = this.getAll();

    if (!_.isEmpty(existingColumns)) {
      const customColumns = _.reject(existingColumns, col => customColumnId === col.id);

      this.setPreferencesRecord(this.recordId, customColumns);
    }
  }

  edit(customColumn) {
    return this.getCustomColumnEntity().post({
      Id: parseInt(customColumn.id, 10),
      Name: customColumn.name,
      Description: customColumn.description,
      CustomColumnExpression: customColumn.expression.join(''),
      Format: customColumn.format,
    }).toPromise().then(({ value }) => {
      const id = String(value);
      if (id === customColumn.id) {
        updatePreferenceStoreWithColumn.call(this, customColumn);
      }
      return id;
    });
  }

  // get custom columns from MT and update preference store
  updateCustomColumnPreferenceStore(avoidCache = false) {
    return getCustomColumnsFromMT.call(this, avoidCache).then((latestColumns) => {
      const currentColumns = this.getAll();

      this.setPreferencesRecord(this.recordId, latestColumns);

      const columnGroups = _.groupBy(latestColumns, (newColumn) => {
        const index = _.findIndex(currentColumns, oldColumn => (newColumn.id === oldColumn.id));
        if (index === -1) {
          return 'addedColumns';
        }
        if (_.isEqual(currentColumns[index], newColumn)) {
          return 'noChange';
        }
        return 'editedColumns';
      });

      const removedColumns = _.filter(
        currentColumns,
        oldColumn => _.findIndex(latestColumns, newColumn => (newColumn.id === oldColumn.id)) === -1
      );

      return new Promise((resolve) => {
        resolve({
          removedColumns,
          editedColumns: columnGroups.editedColumns,
          addedColumns: columnGroups.addedColumns,
        });
      });
    });
  }

  getCustomColumnEntity() {
    const queryParam = new QueryParam(window.location);

    queryParam.remove(['adGroupId', 'campaignId']);
    const { currentEntity } = getEntityFromScope(queryParam);

    return currentEntity.CustomColumns;
  }

  getCustomColumnDetailFromMT(id) {
    return this.getCustomColumnEntity().$withKey(id).get().fetch()
      .then((column) => {
        const formatted = {
          description: column.Description,
          expression: _.filter(column.CustomColumnExpression.split(expressionToken), token => token !== '' && token !== ' '),
          format: column.Format,
          id: String(column.Id),
          name: column.Name,
        };
        updatePreferenceStoreWithColumn.call(this, formatted);

        return formatted;
      });
  }
}
